import { useRef, useState } from 'react'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { LoadingState } from '@/components/ui/LoadingState'
import { Modal } from '@/components/ui/Modal'
import { useCampaignEstimate } from '@/hooks/useCampaignEstimate'
import { useCreateCampaign } from '@/hooks/useCreateCampaign'
import { useScheduleCampaign } from '@/hooks/useScheduleCampaign'
import { useSendCampaign } from '@/hooks/useSendCampaign'
import { useUpdateCampaign } from '@/hooks/useUpdateCampaign'
import { ApiError, toApiError } from '@/lib/api/errors'
import type { VenueCampaignSendResult } from '@/lib/venue/campaignTypes'
import { formatAmount } from '@/lib/venue/format'
import {
  promotionBlockCtaLabel,
  promotionConfirmGuideLines,
  promotionEstimateParamsReady,
  promotionTargetSummaryText,
  toPromotionCreateInput,
  toPromotionEstimateParams,
  toPromotionScheduledAt,
  toPromotionUpdateInput,
  type PromotionComposeFormState,
} from '@/lib/venue/promotionCampaignViewModel'

/**
 * 발송 전 최종 확인 모달(요구사항 3·4).
 *
 * 🚨 mount 시 대상 산출을 새로 조회한다(재확인, NOTICE의 `NoticeSendConfirmModal`과 같은 원칙).
 * 🚨 신규 작성 + 예약 모드는 `POST campaigns`에 `scheduledAt`을 실어 한 번에 SCHEDULED로 만든다 —
 * 별도 `PUT .../schedule` 호출이 필요 없다. 반면 기존 DRAFT(이어서 작성)를 예약으로 확정할 때는
 * 내용을 `PUT .../{id}`로 반영한 뒤 `PUT .../schedule`을 호출한다(설계 §API 흐름).
 * 🚨 재시도는 항상 같은 campaignId로 발송/예약 단계만 다시 부른다 — 새 캠페인을 만들지 않는다.
 */
export function PromotionSendConfirmModal({
  venueId,
  form,
  campaignId,
  onClose,
  onSent,
  onScheduled,
}: {
  venueId: string
  form: PromotionComposeFormState
  /** 기존 DRAFT를 "이어서 작성" 중이면 그 id, 신규 작성이면 `null`. */
  campaignId: number | null
  onClose: () => void
  onSent: (campaignId: number) => void
  onScheduled: (campaignId: number) => void
}) {
  const isExistingDraft = campaignId !== null

  const estimateQuery = useCampaignEstimate(
    venueId,
    toPromotionEstimateParams(form),
    promotionEstimateParamsReady(form)
  )
  const createMutation = useCreateCampaign(venueId)
  const updateMutation = useUpdateCampaign(venueId)
  const scheduleMutation = useScheduleCampaign(venueId)
  const sendMutation = useSendCampaign(venueId)

  const campaignIdRef = useRef<number | null>(campaignId)
  const [sendResult, setSendResult] = useState<VenueCampaignSendResult | null>(null)
  const [scheduledDone, setScheduledDone] = useState(false)
  const [generalError, setGeneralError] = useState<ApiError | null>(null)

  const pending =
    createMutation.isPending || updateMutation.isPending || scheduleMutation.isPending || sendMutation.isPending

  async function handleConfirm() {
    setGeneralError(null)
    try {
      let cid = campaignIdRef.current
      if (cid === null) {
        const created = await createMutation.mutateAsync(toPromotionCreateInput(form, 'COMMIT'))
        cid = created.summary.id
        campaignIdRef.current = cid
      } else if (isExistingDraft) {
        // 기존 DRAFT — 확정 시도마다 최신 내용을 반영한다(PUT은 멱등, 재시도해도 안전).
        await updateMutation.mutateAsync({ campaignId: cid, input: toPromotionUpdateInput(form) })
      }

      if (form.sendMode === 'SCHEDULED') {
        if (isExistingDraft) {
          const scheduledAt = toPromotionScheduledAt(form)
          if (scheduledAt) {
            await scheduleMutation.mutateAsync({ campaignId: cid, scheduledAt })
          }
        }
        // 신규 작성은 POST 시점에 이미 scheduledAt이 반영되어 SCHEDULED로 만들어졌다 — 추가 호출 불필요.
        setScheduledDone(true)
      } else {
        const result = await sendMutation.mutateAsync(cid)
        setSendResult(result)
      }
    } catch (error) {
      setGeneralError(toApiError(error))
    }
  }

  if (scheduledDone && campaignIdRef.current !== null) {
    return (
      <Modal title="발송 확인" onClose={onClose}>
        <div className="space-y-4">
          <Alert tone="info" title="예약이 완료되었습니다">
            <p>예정된 시각에 서버가 자동으로 발송합니다. 발송 가능 시간대·정책은 실행 시점에 다시 확인됩니다.</p>
          </Alert>
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={() => {
                onScheduled(campaignIdRef.current as number)
              }}
            >
              확인
            </Button>
          </div>
        </div>
      </Modal>
    )
  }

  if (sendResult?.dispatched) {
    return (
      <Modal title="발송 확인" onClose={onClose}>
        <div className="space-y-4">
          <Alert tone="info" title="발송을 시작했습니다">
            <div className="space-y-1 text-sm">
              <p>실제 발송 건수 {sendResult.successCount.toLocaleString('ko-KR')}건</p>
              {sendResult.chargedCount !== null && sendResult.chargedAmount !== null ? (
                <p>
                  확정 과금 {sendResult.chargedCount.toLocaleString('ko-KR')}건 ·{' '}
                  {formatAmount(sendResult.chargedAmount)}
                </p>
              ) : null}
            </div>
          </Alert>
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={() => {
                onSent(sendResult.campaignId)
              }}
            >
              성과 조회
            </Button>
          </div>
        </div>
      </Modal>
    )
  }

  if (sendResult && !sendResult.dispatched) {
    const retryable = sendResult.blockRetryable === true
    return (
      <Modal title="발송 확인" onClose={onClose}>
        <div className="space-y-4">
          <Alert tone="danger" title="정책 위반으로 발송이 차단됐습니다">
            <p>{sendResult.blockReasonLabel ?? '발송 정책을 확인해 주세요.'}</p>
          </Alert>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              닫기
            </Button>
            {retryable ? (
              <Button
                type="button"
                onClick={() => {
                  setSendResult(null)
                }}
              >
                {promotionBlockCtaLabel(sendResult.blockRetryable)}
              </Button>
            ) : null}
          </div>
        </div>
      </Modal>
    )
  }

  const estimate = estimateQuery.data
  const targetCount = estimate?.targetCount ?? null
  const sendDisabled = pending || estimateQuery.isLoading || targetCount === null || estimate?.estimateAvailable === false

  return (
    <Modal title="발송 확인" onClose={onClose}>
      <div className="space-y-4">
        {estimateQuery.isLoading ? <LoadingState rows={2} label="예상 비용을 확인하는 중" /> : null}

        {estimateQuery.isError ? (
          <Alert tone="danger">
            <p>{toApiError(estimateQuery.error).message}</p>
          </Alert>
        ) : null}

        {estimate ? (
          <div className="space-y-1 rounded-lg bg-slateish-50 px-3 py-2 text-sm text-ink-700">
            <p>대상: {promotionTargetSummaryText(form)}</p>
            <p>예상 발송 건수: {targetCount === null ? '집계 불가' : `${targetCount.toLocaleString('ko-KR')}건`}</p>
            <p>건당 단가: {estimate.unitPriceAmount > 0 ? formatAmount(estimate.unitPriceAmount) : '정책 미설정'}</p>
            <p>예상 총 비용: {formatAmount(estimate.estimatedTotalCost)}</p>
            {!estimate.estimateAvailable ? (
              <p className="text-xs text-danger-500">{estimate.unavailableMessage ?? '대상을 산출할 수 없습니다.'}</p>
            ) : null}
          </div>
        ) : null}

        <div className="rounded-lg border border-slateish-200 p-3">
          <p className="text-sm font-semibold text-ink-700">{form.title}</p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-slateish-600">{form.body}</p>
        </div>

        <Alert tone="danger">
          <ul className="list-disc space-y-1 pl-4">
            {promotionConfirmGuideLines(estimate).map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </Alert>

        {generalError ? (
          <Alert tone="danger">
            <p>{generalError.message}</p>
          </Alert>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={pending}>
            취소
          </Button>
          <Button
            type="button"
            disabled={sendDisabled}
            onClick={() => {
              void handleConfirm()
            }}
          >
            {pending ? '처리 중…' : form.sendMode === 'SCHEDULED' ? '예약 확정' : '확정'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
